import { useMutation, useQueryClient } from "@tanstack/react-query";
import { cancelRender } from "../api";
import { useT } from "../i18n";
import MutationError from "./MutationError";

/**
 * StopNarration cancels a render that is still running.
 *
 * A long chapter is hundreds of provider calls at the current chunk size, so
 * stopping one has to be possible without waiting for it to spend. Parts that
 * already finished are kept, and Narrate picks up the rest.
 */
export default function StopNarration({ renderID }: { renderID: string }) {
  const client = useQueryClient();
  const t = useT();
  const stop = useMutation({
    mutationFn: () => cancelRender(renderID),
    onSuccess: (render) => {
      // The page polls this key; seeding it ends the poll at once rather than
      // one interval later.
      client.setQueryData(["render", renderID], render);
    },
  });

  return (
    <>
      <button
        type="button"
        data-testid="stop-narration"
        onClick={() => stop.mutate()}
        disabled={stop.isPending}
        className="ms-2 rounded-control border border-line px-2 py-0.5 text-xs text-ink-soft transition-colors hover:bg-sunk disabled:opacity-40"
      >
        {t.narration.stop}
      </button>
      {/* The worst one to leave silent: a render that is still spending, and a
          button that looks like it worked. */}
      <MutationError
        className="mt-1"
        testId="stop-error"
        error={stop.error}
        fallback={t.narration.stopFailed}
      />
    </>
  );
}
